import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { TOLL_EXTRAORDINARY_ASSIGNMENT_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import TollExtraordinaryAssignmentForm from './TollExtraordinaryAssignmentForm';
import {
  getTollExtraordinaryAssignment,
  putTollExtraordinaryAssignment,
} from '../../../../actions';
import { utcDayBoundary, dateOnlyFromUtc } from '../../../../utils/date';

const EditTollExtraordinaryAssignment = ({
  breadcrumbs,
  dispatchGetTollExtraordinaryAssignment,
  dispatchPutTollExtraordinaryAssignment,
  match: {
    params: { id: tollExtraordinaryAssignmentId },
  },
  loading,
  tollExtraordinaryAssignment,
}) => {
  useLayoutEffect(() => {
    dispatchGetTollExtraordinaryAssignment({ tollExtraordinaryAssignmentId });
  }, [dispatchGetTollExtraordinaryAssignment, tollExtraordinaryAssignmentId]);

  const onSubmit = (formValues) => {
    const newFormValues = {
      serviceTypeIds: formValues.serviceTypeIds.map(({ value }) => value),
      routeId: formValues.routeId.value,
      startDate: utcDayBoundary({ date: formValues.startDate, time: 'start' }),
      endDate: utcDayBoundary({ date: formValues.endDate, time: 'end' }),
      tollIds: formValues.tollIds.map(({ value }) => value),
    };

    dispatchPutTollExtraordinaryAssignment(
      tollExtraordinaryAssignmentId,
      newFormValues,
    );
  };

  const generateInitialValues = ({
    routeId,
    routeName,
    serviceType,
    startDate,
    endDate,
    tolls,
  }) => ({
    routeId: {
      value: routeId,
      label: routeId ? `${routeId} - ${routeName || ''}` : '',
    },
    serviceTypeIds: serviceType
      ? [{ value: serviceType.id, label: serviceType.name }]
      : [],
    startDate: startDate && dateOnlyFromUtc(startDate),
    endDate: endDate && dateOnlyFromUtc(endDate),
    tollIds: (tolls || []).map(({ id, name }) => ({
      value: id,
      label: name,
    })),
  });

  let content;

  if (loading) {
    content = <Loader />;
  } else if (tollExtraordinaryAssignment.isEmpty()) {
    content = (
      <NoDataResource returnPage={TOLL_EXTRAORDINARY_ASSIGNMENT_PATH} />
    );
  } else {
    content = (
      <TollExtraordinaryAssignmentForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(
          tollExtraordinaryAssignment.toJS(),
        )}
      />
    );
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Peaje Extraordinario Asignado"
      subtitle="Editar asignación de peajes extraordinarios"
      content={content}
    />
  );
};

EditTollExtraordinaryAssignment.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetTollExtraordinaryAssignment: PropTypes.func.isRequired,
  dispatchPutTollExtraordinaryAssignment: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  tollExtraordinaryAssignment: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditTollExtraordinaryAssignment.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchGetTollExtraordinaryAssignment: getTollExtraordinaryAssignment,
  dispatchPutTollExtraordinaryAssignment: putTollExtraordinaryAssignment,
};

const mapStateToProps = (
  { RouteUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...RouteUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Peajes Extraordinarios Asignados',
      href: TOLL_EXTRAORDINARY_ASSIGNMENT_PATH,
    },
    {
      text: 'Ver',
      href: `${TOLL_EXTRAORDINARY_ASSIGNMENT_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  tollExtraordinaryAssignment: RouteUnit.TollExtraordinaryAssignment.getIn([
    'current',
    'content',
  ]),
  loading: !RouteUnit.TollExtraordinaryAssignment.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditTollExtraordinaryAssignment);
